import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription, SheetHeader } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import { LogOut, LayoutDashboard, FilePlus2, ShieldCheck, Building2, Users, Mail, History, Inbox, Menu, Gauge, ListTodo } from "lucide-react";

export default function AppShell({ children }) {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Phase D: "New Request" is now gated by the reports.order permission.
  // NTN staff always retain the control (they have their own order surfaces).
  const canOrder = user?.role === "ntn_staff" || hasPermission?.("reports.order");

  const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    ...(canOrder
      ? [{ to: "/screenings/new", label: "New Request", icon: FilePlus2, testid: "nav-new-request" }]
      : []),
    ...((user?.role === "ntn_staff" || canOrder)
      ? [{ to: "/outreach", label: "Send Link", icon: Mail, testid: "nav-outreach" }]
      : []),
    ...(user?.role === "ntn_staff"
      ? [
          { to: "/staff-ops", label: "Operations", icon: Gauge, testid: "nav-operations" },
          { to: "/staff-ops/cases", label: "Case Queue", icon: ListTodo, testid: "nav-case-queue" },
          { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "nav-admin-leads" },
          { to: "/admin/clients", label: "Clients", icon: Building2, testid: "nav-admin-clients" },
          { to: "/admin/staff", label: "NTN Team", icon: Users, testid: "nav-admin-staff" },
          { to: "/admin/deletion-log", label: "Audit Log", icon: History, testid: "nav-admin-deletion-log" },
        ]
      : []),
    { to: "/account", label: "Security", icon: ShieldCheck, testid: "nav-account" },
  ];

  const initials = (user?.name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "·";

  const isActive = (to) => location.pathname === to
    || (to !== "/dashboard" && location.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex flex-col bg-slate-50/60 text-slate-900 font-sans">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          {/* Mobile hamburger — visible below md */}
          <div className="flex items-center gap-2 md:gap-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="md:hidden -ml-1 rounded-md text-slate-700 hover:bg-slate-100"
                  data-testid="mobile-nav-toggle"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-white" data-testid="mobile-nav-drawer">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-slate-200 text-left">
                  <SheetTitle className="ntn-h3 text-slate-900">Navigation</SheetTitle>
                  <SheetDescription className="ntn-body-sm text-slate-500">
                    {user?.name} · {user?.role?.replace("_", " ")}
                  </SheetDescription>
                </SheetHeader>
                <nav className="flex flex-col py-3">
                  {navItems.map((n) => {
                    const active = isActive(n.to);
                    return (
                      <Link
                        key={n.label}
                        to={n.to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`mobile-${n.testid}`}
                        className={`flex items-center gap-3 px-6 py-3 text-base font-medium transition-colors
                          ${active
                            ? "bg-blue-50 text-blue-700 border-l-2 border-blue-600"
                            : "text-slate-700 hover:bg-slate-50 border-l-2 border-transparent"}`}
                      >
                        <n.icon className={`h-5 w-5 ${active ? "text-blue-600" : "text-slate-500"}`} />
                        <span>{n.label}</span>
                      </Link>
                    );
                  })}
                </nav>
                <div className="px-6 py-4 border-t border-slate-200 mt-2">
                  <Button
                    onClick={() => { setMobileOpen(false); handleLogout(); }}
                    variant="outline"
                    className="w-full justify-start rounded-md text-slate-700"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <Link to="/dashboard" data-testid="header-home-link" className="shrink-0">
              <Logo />
            </Link>
            {/* Phase E.4: subtle company chip — client users only. Never
                rendered for NTN staff so their surface remains platform-branded. */}
            {user?.role === "property_manager" && user?.company_display_label && (
              <span
                className="hidden lg:inline-flex items-center gap-1.5 pl-3 ml-1 border-l border-slate-200 text-[11px] font-mono tracking-[0.14em] uppercase text-[#4A5568]"
                data-testid="shell-company-chip"
                title={user.company_display_label}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#F59E0B]"></span>
                <span className="truncate max-w-[220px]">{user.company_display_label}</span>
              </span>
            )}
          </div>

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 min-w-0 justify-end overflow-x-auto">
            {navItems.map((n) => {
              const active = isActive(n.to);
              return (
                <Link
                  key={n.label}
                  to={n.to}
                  data-testid={n.testid}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors shrink-0 whitespace-nowrap
                    ${active
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"}`}
                >
                  <n.icon className={`h-4 w-4 ${active ? "text-blue-600" : ""}`} />
                  <span className="hidden xl:inline">{n.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            {user && <NotificationBell />}
            <div className="hidden lg:flex items-center gap-2.5 pl-2 ml-1 border-l border-slate-200">
              <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center" data-testid="header-user-avatar">
                {initials}
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-slate-900 leading-tight" data-testid="header-user-name">{user?.name}</div>
                <div className="ntn-eyebrow leading-tight" data-testid="header-user-role">
                  {user?.role?.replace("_", " ")}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="hidden lg:inline-flex rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">{children}</main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
          <div>
            <span className="font-semibold text-slate-700">NTN Verify Hub</span> — Verified Applications. Confident Decisions.
          </div>
          <div>© {new Date().getFullYear()} National Tenant Network · Trusted screening since 1980.</div>
        </div>
      </footer>
    </div>
  );
}
